import { createSelector } from '@reduxjs/toolkit';
import { selectHostFormFieldValue, selectHostFormFields } from 'features/stepByStepProtocol/store/hostSelectors';
import { PROTOCOL_STATUS } from 'features/stepByStepProtocol/utils/protocol/constants';
import { buildProtocolSuggestions } from 'features/stepByStepProtocol/utils/protocol/buildProtocolSuggestions';
import { EMPTY_PROTOCOL_AUTOMATION, toProtocolAutomationEntries } from 'features/stepByStepProtocol/utils/protocol/protocolAutomation';
import { resolveProtocolPath } from 'features/stepByStepProtocol/utils/protocol/resolveProtocolPath';

const selectSlice = (state) => state.stepByStepProtocol;

export const selectProtocolEntryByKey = (state, protocolKey) => selectSlice(state).protocols[protocolKey] ?? null;

const selectTransactionEntry = (state, transactionId) => selectSlice(state).transactions[transactionId] ?? null;

export const selectProtocolKeyByTransactionId = (state, transactionId) => selectTransactionEntry(state, transactionId)?.protocolKey ?? null;

// NOT_CONFIGURED: the transaction's location has no protocol (no rail item, nothing is read).
// LOADING: attached, but the first read has not been dispatched or has not settled yet.
export const selectProtocolStatusByTransactionId = (state, transactionId) => {
    const protocolKey = selectProtocolKeyByTransactionId(state, transactionId);
    if (!protocolKey) return PROTOCOL_STATUS.NOT_CONFIGURED;
    return selectProtocolEntryByKey(state, protocolKey)?.status ?? PROTOCOL_STATUS.LOADING;
};

// Only an AVAILABLE protocol has a definition (the slice never stores one for any other status).
export const selectProtocolDefinitionByTransactionId = (state, transactionId) => {
    const protocolKey = selectProtocolKeyByTransactionId(state, transactionId);
    if (!protocolKey) return null;
    return selectProtocolEntryByKey(state, protocolKey)?.definition ?? null;
};

export const selectIsProtocolPanelOpen = (state, transactionId) => !!selectTransactionEntry(state, transactionId)?.panelOpen;

// The value of the field the protocol switches on (`vehicle.transportType`) in the transaction's form.
export const selectProtocolSwitchValueByTransactionId = (state, transactionId) => {
    const field = selectProtocolDefinitionByTransactionId(state, transactionId)?.vehicleSwitch?.field;
    return field ? selectHostFormFieldValue(state, transactionId, field) : undefined;
};

// Protocol automation entries per definition, form config and switch value. Weak keys on the definition
// and on the form's `fields` array (set once per transaction), so nothing outlives a transaction, and
// every input of a form reads the same objects.
const automationCache = new WeakMap();

const getOrCreate = (cache, key, create) => {
    if (!cache.has(key)) cache.set(key, create());
    return cache.get(key);
};

const protocolAutomationFor = (definition, formFields, value) => {
    const byValue = getOrCreate(
        getOrCreate(automationCache, definition, () => new WeakMap()),
        formFields,
        () => new Map()
    );
    const key = value ?? '';
    return getOrCreate(byValue, key, () => toProtocolAutomationEntries(buildProtocolSuggestions(resolveProtocolPath(definition, value), formFields)));
};

// `{ [fieldName]: { sourceValue: '/', source: 'Protocol' } }` for the AIQ form fields the transaction's
// protocol path leaves out.
export const selectProtocolAutomationByTransactionId = (state, transactionId) => {
    const definition = selectProtocolDefinitionByTransactionId(state, transactionId);
    const formFields = selectHostFormFields(state, transactionId);
    if (!definition || !Array.isArray(formFields)) return EMPTY_PROTOCOL_AUTOMATION;
    return protocolAutomationFor(definition, formFields, selectProtocolSwitchValueByTransactionId(state, transactionId));
};

export const selectProtocolAutomationForField = (state, transactionId, fieldName) =>
    selectProtocolAutomationByTransactionId(state, transactionId)[fieldName] ?? null;

/**
 * One memoized selector per panel instance (`useMemo(makeSelectProtocolPath, [])`): the path is
 * resolved again only when the definition or the switch field's value changes, so typing in any other
 * field of the form recomputes nothing (FE plan section 10).
 */
export const makeSelectProtocolPath = () =>
    createSelector([selectProtocolDefinitionByTransactionId, selectProtocolSwitchValueByTransactionId], (definition, value) =>
        definition ? resolveProtocolPath(definition, value) : null
    );
